import React, { useCallback, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import {
  Bold, Italic, Strikethrough, List, ListOrdered, Quote, Undo, Redo,
  Heading2, Heading3, Link as LinkIcon, Image as ImageIcon, Minus, Code, Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../App';
import { buildBlogEmbed } from '../../utils/qmgAssets';

const ToolbarBtn = ({ onClick, active, disabled, label, children, testid }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={label}
    aria-label={label}
    data-testid={testid}
    className={`p-2 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
      active
        ? 'bg-[#2C5530] text-white'
        : 'text-[#57534E] hover:bg-[#F2F0E9] hover:text-[#1A1A1A]'
    }`}
  >
    {children}
  </button>
);

const RichTextEditor = ({ value, onChange, placeholder = 'Start writing your post…' }) => {
  const fileInputRef = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Image.configure({ inline: false, allowBase64: false, HTMLAttributes: { class: 'rounded my-4 max-w-full' } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { class: 'text-[#2C5530] underline' } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    onUpdate: ({ editor: ed }) => {
      onChange(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] px-4 py-3',
        'data-testid': 'rte-content',
      },
    },
  });

  const triggerImageUpload = () => fileInputRef.current?.click();

  const handleImageSelected = useCallback(async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !editor) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Image is too large. Max 8MB.');
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    const tId = toast.loading('Uploading image…');
    try {
      const resp = await API.post('/admin/blog/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = resp.data?.url;
      if (!url) throw new Error('No URL returned');
      editor.chain().focus().setImage({ src: url, alt: file.name }).run();
      toast.success('Image inserted.', { id: tId });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed.', { id: tId });
    }
  }, [editor]);

  const handleSetLink = useCallback(() => {
    if (!editor) return;
    const previous = editor.getAttributes('link').href;
    const url = window.prompt('Link URL', previous || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url, target: '_blank' }).run();
  }, [editor]);

  if (!editor) return null;

  return (
    <div className="border border-[#E7E5E4] rounded-sm bg-white overflow-hidden" data-testid="rich-text-editor">
      <div className="flex flex-wrap items-center gap-1 px-2 py-2 border-b border-[#E7E5E4] bg-[#FAFAF9] sticky top-0 z-10">
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive('heading', { level: 2 })} label="Heading 2" testid="rte-h2"><Heading2 className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive('heading', { level: 3 })} label="Heading 3" testid="rte-h3"><Heading3 className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E7E5E4] mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive('bold')} label="Bold" testid="rte-bold"><Bold className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive('italic')} label="Italic" testid="rte-italic"><Italic className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleStrike().run()} active={editor.isActive('strike')} label="Strikethrough" testid="rte-strike"><Strikethrough className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleCode().run()} active={editor.isActive('code')} label="Inline code" testid="rte-code"><Code className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E7E5E4] mx-1" />
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive('bulletList')} label="Bulleted list" testid="rte-ul"><List className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive('orderedList')} label="Numbered list" testid="rte-ol"><ListOrdered className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive('blockquote')} label="Blockquote" testid="rte-quote"><Quote className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().setHorizontalRule().run()} label="Horizontal rule" testid="rte-hr"><Minus className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E7E5E4] mx-1" />
        <ToolbarBtn onClick={handleSetLink} active={editor.isActive('link')} label="Link" testid="rte-link"><LinkIcon className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={triggerImageUpload} label="Insert image" testid="rte-image"><ImageIcon className="w-4 h-4" /></ToolbarBtn>
        <span className="w-px h-5 bg-[#E7E5E4] mx-1" />
        <ToolbarBtn
          onClick={() => editor.chain().focus().insertContent(buildBlogEmbed()).run()}
          label="Insert QMG Blueprint symbols"
          testid="rte-qmg"
        >
          <span className="inline-flex items-center gap-1 px-1">
            <Sparkles className="w-4 h-4" />
            <span className="text-[10px] font-bold uppercase tracking-wider">QMG</span>
          </span>
        </ToolbarBtn>
        <span className="ml-auto" />
        <ToolbarBtn onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} label="Undo" testid="rte-undo"><Undo className="w-4 h-4" /></ToolbarBtn>
        <ToolbarBtn onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} label="Redo" testid="rte-redo"><Redo className="w-4 h-4" /></ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleImageSelected}
        data-testid="rte-image-input"
      />
    </div>
  );
};

export default RichTextEditor;
